import { type ReactNode, useMemo, useRef, useState } from "react";
import {
    ComposedChart,
    Area,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Legend,
} from "recharts";
import { Download, Loader2 } from "lucide-react";
import html2canvas from "html2canvas";
import { AccountBreakdown } from "../core/_requests_v2";
import { generateProjectionReport, AccountWithValues } from "../utils/generateProjectionReport";
import {
    getStrategicContextSections,
    getAssumptionItems,
    type Segment,
    type RichText,
} from "../utils/reportContent";
import dayjs from "dayjs";

interface OnboardingProjectionProps {
    accounts: AccountBreakdown[];
    accountTableData: AccountWithValues[];
    startDate: string;
    endDate: string;
    closeRate: number;
    policyValue: number;
    insuranceMargin: number;
    generalConversion: number;
    collectionRate: number;
    totalAccounts?: number;
    totalSignatures?: number;
    children?: ReactNode;
}

type ProjectionPeriod = 3 | 5 | 7;
type AdoptionRamp = 0 | 3 | 6;

interface ProjectionDataPoint {
    month: number;
    label: string;
    activeAccounts: number;
    effectiveAccounts: number;
    monthlySignatures: number;
    monthlyPreneed: number;
    monthlyConversions: number;
    monthlyRevenue: number;
    monthlyProfit: number;
    cumulativeSignatures: number;
    cumulativeConversions: number;
    cumulativeRevenue: number;
    cumulativeProfit: number;
}

function formatLargeNumber(value: number): string {
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${Math.round(value / 1_000).toLocaleString()}K`;
    return `$${value.toLocaleString()}`;
}

function formatCount(value: number): string {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${Math.round(value / 1_000).toLocaleString()}K`;
    return value.toLocaleString();
}

// Compute projection profit for a given set of assumptions
function computeProjectionProfit(
    totalMonths: number,
    targetAccounts: number,
    signaturesPerMonth: number,
    preneedRate: number,
    closeRatePct: number,
    generalConvPct: number,
    policyVal: number,
    marginPct: number,
    rampMonths: number,
    collectionPct: number,
): number {
    const midpoint = totalMonths / 2;
    const k = Math.log(49) / midpoint;
    const rate = closeRatePct / 100;
    const genRate = generalConvPct / 100;
    const marginRate = marginPct / 100;
    const collectionFactor = collectionPct / 100;

    let cumulativeProfit = 0;

    // Pre-compute sigmoid values for ramp calculation
    const sigmoidValues: number[] = [];
    for (let m = -rampMonths; m <= totalMonths; m++) {
        sigmoidValues.push(m < 0 ? 0 : targetAccounts / (1 + Math.exp(-k * (m - midpoint))));
    }
    const offset = rampMonths; // index offset so month 0 = sigmoidValues[offset]

    for (let month = 0; month <= totalMonths; month++) {
        const totalActive = sigmoidValues[month + offset];

        let effectiveAccounts = totalActive;
        if (rampMonths > 0) {
            let rampReduction = 0;
            for (let age = 0; age < rampMonths; age++) {
                const addedThisMonth = sigmoidValues[month + offset - age] - sigmoidValues[month + offset - age - 1];
                if (addedThisMonth > 0) {
                    const rampFraction = (age + 1) / rampMonths;
                    rampReduction += addedThisMonth * (1 - rampFraction);
                }
            }
            effectiveAccounts = totalActive - rampReduction;
        }

        const monthlySignatures = effectiveAccounts * signaturesPerMonth;
        const monthlyPreneed = monthlySignatures * preneedRate;
        const highIntent = monthlyPreneed * rate;
        const general = (monthlySignatures - monthlyPreneed) * genRate;
        const monthlyConversions = highIntent + general;
        const monthlyRevenue = monthlyConversions * policyVal * collectionFactor;
        cumulativeProfit += monthlyRevenue * marginRate;
    }

    return cumulativeProfit;
}

// ---- Rich text rendering helpers ----

function renderSegment(segment: Segment, key: number) {
    if (typeof segment === 'string') return <span key={key}>{segment}</span>;
    if ('bold' in segment) return <span key={key} className="font-semibold text-gray-900">{segment.bold}</span>;
    if ('cite' in segment) return <a key={key} href={segment.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs align-super">[{segment.cite}]</a>;
    if ('link' in segment) return <a key={key} href={segment.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{segment.link}</a>;
    return null;
}

function renderRichText(paragraph: RichText, key: string, className?: string) {
    return (
        <p key={key} className={className}>
            {paragraph.map((seg, i) => renderSegment(seg, i))}
        </p>
    );
}

export function OnboardingProjection({
    accounts,
    accountTableData,
    startDate,
    endDate,
    closeRate,
    policyValue,
    insuranceMargin,
    generalConversion,
    collectionRate,
    totalAccounts,
    totalSignatures,
    children,
}: OnboardingProjectionProps) {
    const chartRef = useRef<HTMLDivElement>(null);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [targetAccounts, setTargetAccounts] = useState<number>(() => {
        const stored = localStorage.getItem("onboardingProjection-targetAccounts");
        return stored ? Number(stored) : 600;
    });
    const [projectionYears, setProjectionYears] = useState<ProjectionPeriod>(() => {
        const stored = localStorage.getItem("onboardingProjection-projectionYears");
        return stored ? (Number(stored) as ProjectionPeriod) : 5;
    });
    const [adoptionRamp, setAdoptionRamp] = useState<AdoptionRamp>(() => {
        const stored = localStorage.getItem("onboardingProjection-adoptionRamp");
        return stored ? (Number(stored) as AdoptionRamp) : 0;
    });
    const [customTarget, setCustomTarget] = useState<string>(String(targetAccounts));

    // Compute baseline from median account
    const baseline = useMemo(() => {
        if (accounts.length === 0) return null;

        const sorted = [...accounts].sort((a, b) => a.signature_count - b.signature_count);
        const midIdx = Math.floor(sorted.length / 2);
        const medianAccount =
            sorted.length % 2 === 0
                ? sorted[midIdx] // for even, use upper median (more conservative)
                : sorted[midIdx];

        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const monthsInRange = Math.max(1, end.diff(start, "month", true));

        const signaturesPerMonth = medianAccount.signature_count / monthsInRange;
        const preneedRate =
            medianAccount.signature_count > 0
                ? medianAccount.preneed_count / medianAccount.signature_count
                : 0;

        return {
            account: medianAccount,
            monthsInRange,
            signaturesPerMonth,
            preneedRate,
        };
    }, [accounts, startDate, endDate]);

    // S-curve projection data with optional adoption ramp
    const projectionData = useMemo(() => {
        if (!baseline) return [];

        const totalMonths = projectionYears * 12;
        const midpoint = totalMonths / 2;
        const k = Math.log(49) / midpoint;

        const rate = closeRate / 100;
        const genRate = generalConversion / 100;
        const marginRate = insuranceMargin / 100;
        const collectionFactor = collectionRate / 100;

        // Pre-compute sigmoid values (need history for ramp)
        const sigmoidValues: number[] = [];
        for (let m = -adoptionRamp; m <= totalMonths; m++) {
            sigmoidValues.push(m < 0 ? 0 : targetAccounts / (1 + Math.exp(-k * (m - midpoint))));
        }
        const offset = adoptionRamp;

        const data: ProjectionDataPoint[] = [];
        let cumulativeSignatures = 0;
        let cumulativeConversions = 0;
        let cumulativeRevenue = 0;
        let cumulativeProfit = 0;

        for (let month = 0; month <= totalMonths; month++) {
            const totalActive = sigmoidValues[month + offset];

            // Apply per-account adoption ramp
            let effectiveAccounts = totalActive;
            if (adoptionRamp > 0) {
                let rampReduction = 0;
                for (let age = 0; age < adoptionRamp; age++) {
                    const addedThisMonth = sigmoidValues[month + offset - age] - sigmoidValues[month + offset - age - 1];
                    if (addedThisMonth > 0) {
                        const rampFraction = (age + 1) / adoptionRamp;
                        rampReduction += addedThisMonth * (1 - rampFraction);
                    }
                }
                effectiveAccounts = totalActive - rampReduction;
            }

            const monthlySignatures = effectiveAccounts * baseline.signaturesPerMonth;
            const monthlyPreneed = monthlySignatures * baseline.preneedRate;
            const highIntent = monthlyPreneed * rate;
            const general = (monthlySignatures - monthlyPreneed) * genRate;
            const monthlyConversions = highIntent + general;
            const monthlyRevenue = monthlyConversions * policyValue * collectionFactor;
            const monthlyProfit = monthlyRevenue * marginRate;

            cumulativeSignatures += monthlySignatures;
            cumulativeConversions += monthlyConversions;
            cumulativeRevenue += monthlyRevenue;
            cumulativeProfit += monthlyProfit;

            data.push({
                month,
                label: month % 12 === 0 ? `Year ${month / 12}` : `M${month}`,
                activeAccounts: Math.round(totalActive),
                effectiveAccounts: Math.round(effectiveAccounts),
                monthlySignatures: Math.round(monthlySignatures),
                monthlyPreneed: Math.round(monthlyPreneed),
                monthlyConversions: Math.round(monthlyConversions),
                monthlyRevenue: Math.round(monthlyRevenue),
                monthlyProfit: Math.round(monthlyProfit),
                cumulativeSignatures: Math.round(cumulativeSignatures),
                cumulativeConversions: Math.round(cumulativeConversions),
                cumulativeRevenue: Math.round(cumulativeRevenue),
                cumulativeProfit: Math.round(cumulativeProfit),
            });
        }

        return data;
    }, [baseline, targetAccounts, projectionYears, closeRate, policyValue, insuranceMargin, generalConversion, collectionRate, adoptionRamp]);

    // Final totals from last data point
    const finalTotals = projectionData.length > 0 ? projectionData[projectionData.length - 1] : null;

    // Sensitivity table: varies close rate (rows) x general conversion (columns), with current margin
    const sensitivityData = useMemo(() => {
        if (!baseline) return null;

        const totalMonths = projectionYears * 12;
        const closeRates = [20, 35, 50];
        const generalRates = [0.5, 1, 2, 3];
        const margins = [15, 25, 35];

        const grid: { closeRate: number; generalRate: number; margin: number; profit: number }[] = [];

        for (const cr of closeRates) {
            for (const gr of generalRates) {
                for (const mg of margins) {
                    const profit = computeProjectionProfit(
                        totalMonths, targetAccounts, baseline.signaturesPerMonth,
                        baseline.preneedRate, cr, gr, policyValue, mg, adoptionRamp, collectionRate,
                    );
                    grid.push({ closeRate: cr, generalRate: gr, margin: mg, profit });
                }
            }
        }

        return { closeRates, generalRates, margins, grid };
    }, [baseline, targetAccounts, projectionYears, policyValue, collectionRate, adoptionRamp]);

    const handleTargetPreset = (value: number) => {
        setTargetAccounts(value);
        setCustomTarget(String(value));
        localStorage.setItem("onboardingProjection-targetAccounts", String(value));
    };

    const handleCustomTargetBlur = () => {
        const n = parseInt(customTarget, 10);
        if (n > 0) {
            setTargetAccounts(n);
            localStorage.setItem("onboardingProjection-targetAccounts", String(n));
        } else {
            setCustomTarget(String(targetAccounts));
        }
    };

    const handlePeriodChange = (years: ProjectionPeriod) => {
        setProjectionYears(years);
        localStorage.setItem("onboardingProjection-projectionYears", String(years));
    };

    const handleRampChange = (months: AdoptionRamp) => {
        setAdoptionRamp(months);
        localStorage.setItem("onboardingProjection-adoptionRamp", String(months));
    };

    const handleDownloadReport = async () => {
        if (!baseline || !finalTotals || !sensitivityData) return;
        setIsGeneratingPdf(true);
        try {
            let chartImageData: string | null = null;
            if (chartRef.current) {
                const canvas = await html2canvas(chartRef.current, {
                    scale: 2,
                    backgroundColor: "#fff",
                    logging: false,
                });
                chartImageData = canvas.toDataURL("image/png");
            }
            generateProjectionReport({
                baseline,
                finalTotals,
                sensitivityData,
                chartImageData,
                accountTableData,
                targetAccounts,
                projectionYears,
                adoptionRamp,
                closeRate,
                generalConversion,
                policyValue,
                insuranceMargin,
                collectionRate,
                startDate,
                endDate,
                totalAccounts,
                totalSignatures,
            });
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    if (!baseline || accounts.length === 0) return null;

    return (
        <div>
            {/* Section divider */}
            <div className="flex items-center gap-3 mb-5">
                <div className="h-px flex-1 bg-gray-200" />
                <h2 className="text-lg font-semibold text-gray-800">Onboarding Projection</h2>
                {finalTotals && sensitivityData && (
                    <button
                        onClick={handleDownloadReport}
                        disabled={isGeneratingPdf}
                        className="btn btn-sm btn-primary flex items-center gap-1.5"
                    >
                        {isGeneratingPdf ? (
                            <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                <span>Generating...</span>
                            </>
                        ) : (
                            <>
                                <Download className="w-3.5 h-3.5" />
                                <span>Download Report</span>
                            </>
                        )}
                    </button>
                )}
                <div className="h-px flex-1 bg-gray-200" />
            </div>

            {/* Executive Summary */}
            {finalTotals && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg px-6 py-5 mb-4">
                    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-3">Executive Summary</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                        This projection models the insurance revenue opportunity from onboarding{" "}
                        <span className="font-bold text-gray-900">{targetAccounts.toLocaleString()} funeral homes</span> onto
                        the Signature Guestbook platform over{" "}
                        <span className="font-bold text-gray-900">{projectionYears} years</span>.
                        Using our median account ({baseline.account.account_name}: {baseline.signaturesPerMonth.toFixed(1)} signatures/month,{" "}
                        {(baseline.preneedRate * 100).toFixed(1)}% preneed rate) as a baseline, the model projects{" "}
                        <span className="font-bold text-green-700 text-base">{formatLargeNumber(finalTotals.cumulativeProfit)}</span> in
                        total profit from{" "}
                        <span className="font-bold text-blue-700 text-base">{formatLargeNumber(finalTotals.cumulativeRevenue)}</span> in
                        premium revenue across{" "}
                        <span className="font-bold text-gray-900">{formatCount(finalTotals.cumulativeConversions)} policies</span>.
                        Onboarding follows an S-curve adoption model reflecting realistic enterprise rollout dynamics.
                        {adoptionRamp > 0 && ` Each new account ramps to full output over ${adoptionRamp} months.`}
                        {" "}See the sensitivity analysis below for profit ranges under different assumption sets.
                    </p>
                </div>
            )}

            {/* Strategic Context */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-5 mb-4">
                <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-3">Strategic Context</h3>
                <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
                    {getStrategicContextSections().flatMap((section, si) =>
                        section.body.map((para, pi) => renderRichText(para, `sc-${si}-${pi}`))
                    )}
                </div>
            </div>

            {/* Injected content (controls + account table from parent) */}
            {children}

            {/* Baseline info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4">
                <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-blue-800">
                        <span className="font-medium">Baseline: {baseline.account.account_name}</span>
                        <span className="text-blue-600 ml-1">(median account)</span>
                        <span className="mx-2 text-blue-300">|</span>
                        <span>{baseline.signaturesPerMonth.toFixed(1)} signatures/mo</span>
                        <span className="mx-2 text-blue-300">|</span>
                        <span>{(baseline.preneedRate * 100).toFixed(1)}% preneed rate</span>
                        <span className="mx-2 text-blue-300">|</span>
                        <span>{baseline.monthsInRange.toFixed(0)} months of data</span>
                    </div>
                </div>
            </div>

            {/* Controls bar */}
            <div className="card mb-4">
                <div className="card-body py-3">
                    <div className="flex flex-wrap items-center gap-4">
                        {/* Target accounts */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 font-medium">Target Accounts:</span>
                            <div className="flex gap-1 items-center">
                                {[600, 1200, 1800].map((n) => (
                                    <button
                                        key={n}
                                        onClick={() => handleTargetPreset(n)}
                                        className={`px-2.5 py-1 rounded text-xs cursor-pointer transition-colors ${
                                            targetAccounts === n
                                                ? "bg-blue-100 text-blue-700 font-semibold ring-1 ring-blue-400"
                                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                        }`}
                                    >
                                        {n.toLocaleString()}
                                    </button>
                                ))}
                                <input
                                    type="number"
                                    value={customTarget}
                                    onChange={(e) => setCustomTarget(e.target.value)}
                                    onBlur={handleCustomTargetBlur}
                                    onKeyDown={(e) => { if (e.key === "Enter") handleCustomTargetBlur(); }}
                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-xs text-center"
                                    min={1}
                                />
                            </div>
                        </div>

                        {/* Period presets */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 font-medium">Period:</span>
                            <div className="flex gap-1">
                                {([3, 5, 7] as ProjectionPeriod[]).map((y) => (
                                    <button
                                        key={y}
                                        onClick={() => handlePeriodChange(y)}
                                        className={`px-3 py-1 rounded text-xs cursor-pointer transition-colors ${
                                            projectionYears === y
                                                ? "bg-blue-100 text-blue-700 font-semibold ring-1 ring-blue-400"
                                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                        }`}
                                    >
                                        {y}yr
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Adoption ramp */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 font-medium">Adoption Ramp:</span>
                            <div className="flex gap-1">
                                {([
                                    { key: 0 as AdoptionRamp, label: "None" },
                                    { key: 3 as AdoptionRamp, label: "3mo" },
                                    { key: 6 as AdoptionRamp, label: "6mo" },
                                ]).map((r) => (
                                    <button
                                        key={r.key}
                                        onClick={() => handleRampChange(r.key)}
                                        className={`px-2.5 py-1 rounded text-xs cursor-pointer transition-colors ${
                                            adoptionRamp === r.key
                                                ? "bg-blue-100 text-blue-700 font-semibold ring-1 ring-blue-400"
                                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                        }`}
                                    >
                                        {r.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="border-l border-gray-200 h-6" />

                        {/* Read-only assumptions reference */}
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                            <span className="font-medium">Assumptions:</span>
                            <span>{closeRate}% close</span>
                            <span className="text-gray-300">|</span>
                            <span>{generalConversion}% general</span>
                            <span className="text-gray-300">|</span>
                            <span>
                                ${policyValue >= 1000
                                    ? `${(policyValue / 1000).toFixed(policyValue % 1000 === 0 ? 0 : 1)}K`
                                    : policyValue.toLocaleString()}{" "}
                                policy
                            </span>
                            <span className="text-gray-300">|</span>
                            <span>{insuranceMargin}% margin</span>
                            <span className="text-gray-300">|</span>
                            <span>{collectionRate}% collected</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Simple explanation */}
            {finalTotals && (() => {
                const effectivePremium = policyValue * (collectionRate / 100);
                const profitPerPolicy = Math.round(effectivePremium * (insuranceMargin / 100));
                return (
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-5 mb-5">
                    <h3 className="text-base font-semibold text-gray-800 mb-3">How This Works</h3>
                    <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
                        <p>
                            We looked at our current funeral homes and picked the middle performer —{" "}
                            <span className="font-semibold">{baseline.account.account_name}</span> — as a realistic
                            example of what one funeral home produces.
                            That account collects about <span className="font-bold text-gray-900">{Math.round(baseline.signaturesPerMonth)} guestbook signatures every month</span>.
                        </p>
                        <p>
                            Now imagine <span className="font-bold text-gray-900">{targetAccounts.toLocaleString()} funeral homes</span> gradually
                            joining the platform over <span className="font-bold text-gray-900">{projectionYears} years</span>.
                            They don't all join at once — it starts slow, speeds up in the middle, then levels off
                            (like how any big company rolls out new software).
                            {adoptionRamp > 0 && (
                                <> Each new account also takes <span className="font-bold text-gray-900">{adoptionRamp} months to reach full output</span> after going live, reflecting real-world adoption friction.</>
                            )}
                        </p>
                        <p>
                            Each signature is a person attending a funeral. Of those people,{" "}
                            <span className="font-bold text-gray-900">{(baseline.preneedRate * 100).toFixed(1)}%</span> express
                            interest in planning ahead (preneed). We estimate{" "}
                            <span className="font-bold text-gray-900">{closeRate}%</span> of those interested people
                            eventually buy a preneed insurance policy — a whole-life policy with a face
                            value of <span className="font-bold text-gray-900">${policyValue.toLocaleString()}</span>{" "}
                            (LIMRA's 2024 survey reports a $5,398 average{" "}
                            <a href="https://www.limra.com/en/newsroom/news-releases/2025/2025-life-insurers-council-survey-indicates-continued-growth-in-preneed-market/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs align-super">[LIMRA]</a>).
                            These policies can be paid as a single lump sum or in monthly installments over 5-10 years.
                            Not all installment buyers complete their payments, so we apply a{" "}
                            <span className="font-bold text-gray-900">{collectionRate}% collection rate</span> — meaning
                            the effective premium collected per policy is{" "}
                            <span className="font-bold text-gray-900">${Math.round(policyValue * collectionRate / 100).toLocaleString()}</span>.
                            The remaining signatures convert at a lower <span className="font-bold text-gray-900">{generalConversion}%</span> rate.
                        </p>
                        <p>
                            At a <span className="font-bold text-gray-900">{insuranceMargin}% margin</span>, that means
                            over {projectionYears} years, {targetAccounts.toLocaleString()} funeral homes could generate{" "}
                            <span className="font-bold text-green-700 text-lg">{formatLargeNumber(finalTotals.cumulativeProfit)}</span> in
                            profit from{" "}
                            <span className="font-bold text-blue-700 text-lg">{formatLargeNumber(finalTotals.cumulativeRevenue)}</span> in
                            total premium revenue.
                        </p>
                    </div>

                    {/* Why this is real value */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-800 mb-2">Why is this real value for an insurance company?</h4>
                        <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
                            <p>
                                When someone buys a <span className="font-bold text-gray-900">${policyValue.toLocaleString()}</span> preneed
                                policy — whether paid as a lump sum or in monthly installments — the insurance
                                company collects premiums <span className="font-semibold">well before</span> paying
                                the death benefit <span className="font-semibold">years or decades later</span>.
                                During that time, the insurance company invests the money. That long holding
                                period is where the profit comes from — investment returns compound year after year
                                before the payout ever happens.
                            </p>
                            <p>
                                A <span className="font-bold text-gray-900">{insuranceMargin}% margin</span> represents approximately{" "}
                                <span className="font-bold text-gray-900">${profitPerPolicy.toLocaleString()}</span> per
                                policy in total economic return. This figure is a simplified proxy that includes the benefit of
                                the long investment runway — premiums collected now, claims paid years later — plus potential
                                gains from policy lapses. Published company-level profit margins for preneed carriers
                                are lower (single-digit to low teens), but the per-policy total economic return can be higher when
                                investment income and lapse gains are factored in.
                            </p>
                            <p>
                                The key insight: <span className="font-semibold">the funeral home is both the lead source and the
                                eventual recipient of the death benefit</span>. The insurance company wins
                                because they get a steady pipeline of high-intent leads at the exact moment people are thinking
                                about end-of-life planning — without having to spend money finding those people.
                                The funeral home wins because they lock in future business. The consumer wins because
                                they lock in today's prices for a future need.
                            </p>
                            <p>
                                So is <span className="font-bold text-green-700 text-lg">{formatLargeNumber(finalTotals.cumulativeProfit)}</span> a
                                reasonable number? Yes — it represents{" "}
                                <span className="font-bold text-gray-900">{formatCount(finalTotals.cumulativeConversions)} policies</span> x{" "}
                                <span className="font-bold text-gray-900">${profitPerPolicy.toLocaleString()} profit each</span>,
                                generated from leads the insurance company would otherwise have to acquire on their own.
                                This platform delivers those leads at scale, passively, from real funeral home visitors
                                who are already in the right mindset.
                                The sensitivity table below shows how these numbers hold up under different assumptions.
                            </p>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* Summary cards */}
            {finalTotals && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div className="card border-l-4 border-l-green-500">
                        <div className="card-body py-4 px-4">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Projected Profit</div>
                            <div className="text-2xl font-bold text-green-700 mt-1">{formatLargeNumber(finalTotals.cumulativeProfit)}</div>
                            <div className="text-xs text-gray-400 mt-1">{projectionYears}-year projection at {insuranceMargin}% margin</div>
                        </div>
                    </div>
                    <div className="card border-l-4 border-l-blue-500">
                        <div className="card-body py-4 px-4">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Premium Revenue</div>
                            <div className="text-2xl font-bold text-blue-700 mt-1">{formatLargeNumber(finalTotals.cumulativeRevenue)}</div>
                            <div className="text-xs text-gray-400 mt-1">{formatCount(finalTotals.cumulativeConversions)} policies x ${Math.round(policyValue * collectionRate / 100).toLocaleString()} effective</div>
                        </div>
                    </div>
                    <div className="card border-l-4 border-l-purple-500">
                        <div className="card-body py-4 px-4">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Projected Policies</div>
                            <div className="text-2xl font-bold text-purple-700 mt-1">{formatCount(finalTotals.cumulativeConversions)}</div>
                            <div className="text-xs text-gray-400 mt-1">{closeRate}% preneed close + {generalConversion}% general</div>
                        </div>
                    </div>
                    <div className="card border-l-4 border-l-amber-500">
                        <div className="card-body py-4 px-4">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Signatures</div>
                            <div className="text-2xl font-bold text-amber-700 mt-1">{formatCount(finalTotals.cumulativeSignatures)}</div>
                            <div className="text-xs text-gray-400 mt-1">
                                {targetAccounts.toLocaleString()} accounts over {projectionYears} years
                                {adoptionRamp > 0 && ` (${adoptionRamp}mo ramp)`}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Chart */}
            <div className="card mb-4" ref={chartRef}>
                <div className="card-body">
                    <ResponsiveContainer width="100%" height={400}>
                        <ComposedChart data={projectionData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                            <defs>
                                <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0.05} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis
                                dataKey="month"
                                tickFormatter={(m: number) => (m % 12 === 0 ? `Year ${m / 12}` : "")}
                                ticks={Array.from({ length: projectionYears + 1 }, (_, i) => i * 12)}
                                tick={{ fontSize: 12, fill: "#9ca3af" }}
                            />
                            <YAxis
                                yAxisId="profit"
                                tickFormatter={(v: number) => {
                                    if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(1)}B`;
                                    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(0)}M`;
                                    if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
                                    return `$${v}`;
                                }}
                                tick={{ fontSize: 11, fill: "#9ca3af" }}
                                width={70}
                            />
                            <YAxis
                                yAxisId="accounts"
                                orientation="right"
                                tickFormatter={(v: number) => v.toLocaleString()}
                                tick={{ fontSize: 11, fill: "#9ca3af" }}
                                width={60}
                            />
                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (!active || !payload || payload.length === 0) return null;
                                    const d = payload[0]?.payload as ProjectionDataPoint;
                                    if (!d) return null;
                                    const yearNum = Math.floor(d.month / 12);
                                    const monthInYear = d.month % 12;
                                    return (
                                        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 text-sm">
                                            <div className="font-semibold text-gray-800 mb-2">
                                                Year {yearNum}, Month {monthInYear} (M{label})
                                            </div>
                                            <div className="space-y-1 text-gray-600">
                                                <div className="flex justify-between gap-4">
                                                    <span>Active Accounts:</span>
                                                    <span className="font-medium text-indigo-700">{d.activeAccounts.toLocaleString()}</span>
                                                </div>
                                                {adoptionRamp > 0 && (
                                                    <div className="flex justify-between gap-4">
                                                        <span>Effective (ramped):</span>
                                                        <span className="font-medium text-indigo-500">{d.effectiveAccounts.toLocaleString()}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between gap-4">
                                                    <span>Monthly Signatures:</span>
                                                    <span className="font-medium">{d.monthlySignatures.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between gap-4">
                                                    <span>Monthly Policies:</span>
                                                    <span className="font-medium">{d.monthlyConversions.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between gap-4">
                                                    <span>Monthly Profit:</span>
                                                    <span className="font-medium text-green-700">{formatLargeNumber(d.monthlyProfit)}</span>
                                                </div>
                                                <hr className="border-gray-200" />
                                                <div className="flex justify-between gap-4 font-semibold">
                                                    <span>Cumulative Profit:</span>
                                                    <span className="text-green-700">{formatLargeNumber(d.cumulativeProfit)}</span>
                                                </div>
                                                <div className="flex justify-between gap-4">
                                                    <span>Cumulative Revenue:</span>
                                                    <span className="text-blue-700">{formatLargeNumber(d.cumulativeRevenue)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }}
                            />
                            <Legend
                                formatter={(value: string) => (
                                    <span className="text-xs text-gray-600">{value}</span>
                                )}
                            />
                            <Area
                                yAxisId="profit"
                                type="monotone"
                                dataKey="cumulativeProfit"
                                name="Cumulative Profit"
                                stroke="#16a34a"
                                strokeWidth={2}
                                fill="url(#profitGradient)"
                            />
                            <Line
                                yAxisId="accounts"
                                type="monotone"
                                dataKey="activeAccounts"
                                name="Active Accounts"
                                stroke="#6366f1"
                                strokeWidth={2}
                                strokeDasharray="6 3"
                                dot={false}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Sensitivity table */}
            {sensitivityData && (
                <div className="card mb-4">
                    <div className="card-body">
                        <h3 className="text-sm font-semibold text-gray-800 mb-1">Sensitivity Analysis</h3>
                        <p className="text-xs text-gray-500 mb-4">
                            Projected {projectionYears}-year profit under different assumption combinations.
                            {adoptionRamp > 0 && ` Includes ${adoptionRamp}-month per-account adoption ramp.`}
                            {" "}Current assumptions are highlighted.
                        </p>

                        {sensitivityData.margins.map((mg) => (
                            <div key={mg} className="mb-5 last:mb-0">
                                <div className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                                    At {mg}% Insurance Margin
                                    {mg === insuranceMargin && <span className="ml-1 text-blue-600 normal-case">(current)</span>}
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm border-collapse">
                                        <thead>
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs text-gray-500 font-medium border-b border-gray-200">
                                                    Close Rate ↓ / General Conv. →
                                                </th>
                                                {sensitivityData.generalRates.map((gr) => (
                                                    <th
                                                        key={gr}
                                                        className={`px-3 py-2 text-right text-xs font-medium border-b border-gray-200 ${
                                                            gr === generalConversion && mg === insuranceMargin
                                                                ? "text-blue-700 bg-blue-50"
                                                                : "text-gray-500"
                                                        }`}
                                                    >
                                                        {gr}%
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sensitivityData.closeRates.map((cr) => (
                                                <tr key={cr} className="border-b border-gray-100">
                                                    <td className={`px-3 py-2 text-xs font-medium ${
                                                        cr === closeRate && mg === insuranceMargin ? "text-blue-700 bg-blue-50" : "text-gray-600"
                                                    }`}>
                                                        {cr}% close
                                                    </td>
                                                    {sensitivityData.generalRates.map((gr) => {
                                                        const cell = sensitivityData.grid.find(
                                                            (g) => g.closeRate === cr && g.generalRate === gr && g.margin === mg
                                                        );
                                                        const isCurrent = cr === closeRate && gr === generalConversion && mg === insuranceMargin;
                                                        return (
                                                            <td
                                                                key={gr}
                                                                className={`px-3 py-2 text-right text-sm ${
                                                                    isCurrent
                                                                        ? "font-bold text-green-700 bg-green-50 ring-2 ring-green-400 ring-inset"
                                                                        : "text-gray-700"
                                                                }`}
                                                            >
                                                                {cell ? formatLargeNumber(cell.profit) : "—"}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Assumptions & Validation */}
            <div className="card mb-4">
                <div className="card-body">
                    <h3 className="text-sm font-semibold text-gray-800 mb-1">Assumptions & Validation</h3>
                    <p className="text-xs text-gray-500 mb-4">
                        What is observed data vs. what is assumed — and the reasoning behind each assumption.
                    </p>

                    <div className="space-y-4">
                        {getAssumptionItems({
                            closeRate,
                            generalConversion,
                            policyValue,
                            insuranceMargin,
                            collectionRate,
                            preneedRate: baseline.preneedRate,
                            accountName: baseline.account.account_name,
                            monthsInRange: baseline.monthsInRange,
                            signaturesPerMonth: baseline.signaturesPerMonth,
                            targetAccounts,
                        }).map((item, idx) => {
                            const borderColor = item.status === 'KEY LIMITATION' ? 'border-red-400'
                                : item.status === 'ASSUMED' ? 'border-amber-400'
                                : item.status === 'OBSERVED' ? 'border-green-400'
                                : 'border-gray-400';
                            const textColor = item.status === 'KEY LIMITATION' ? 'text-red-700'
                                : item.status === 'ASSUMED' ? 'text-amber-700'
                                : item.status === 'OBSERVED' ? 'text-green-700'
                                : 'text-gray-500';
                            return (
                                <div key={idx} className={`border-l-2 ${borderColor} pl-4`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-xs font-semibold ${textColor} uppercase tracking-wide`}>{item.status === 'KEY LIMITATION' ? 'Key Limitation' : item.status === 'ASSUMED' ? 'Assumed' : item.status === 'OBSERVED' ? 'Observed' : 'Qualitative'}</span>
                                        <span className="text-sm font-semibold text-gray-800">{item.title}</span>
                                    </div>
                                    {item.body.map((para, pi) => renderRichText(para, `av-${idx}-${pi}`, "text-sm text-gray-600 leading-relaxed"))}
                                    {item.callout && (
                                        <div className="mt-2 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                                            {item.callout.map((para, ci) => renderRichText(para, `av-${idx}-c${ci}`, "text-sm text-blue-800 leading-relaxed"))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Methodology footnote */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-5 py-4 mb-8">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Methodology</h4>
                <p className="text-sm text-gray-500 leading-relaxed">
                    Projection uses an S-curve (sigmoid) onboarding model: <span className="font-mono text-xs">f(t) = N / (1 + e<sup>-k(t - t<sub>0</sub>)</sup>)</span> where
                    k = ln(49) / midpoint, producing ~2% activation at month 0 and ~98% at period end.
                    This reflects typical enterprise SaaS rollout dynamics.
                    Per-account output is derived from the median account's observed monthly signature
                    rate ({baseline.signaturesPerMonth.toFixed(1)} sigs/mo from {baseline.account.account_name} over {baseline.monthsInRange.toFixed(0)} months).
                    {adoptionRamp > 0 && (
                        <> Each newly onboarded account linearly ramps from 0% to 100% output over {adoptionRamp} months, reflecting real-world adoption friction.</>
                    )}
                    {" "}Conversion model: preneed signatures ({(baseline.preneedRate * 100).toFixed(1)}% of total) x {closeRate}% close rate
                    + general signatures x {generalConversion}% baseline conversion.
                    Policy face value (${policyValue.toLocaleString()}) is based on LIMRA's 2024 survey average.
                    Policies can be paid as a single lump sum or in monthly installments over 5-10 years;
                    a {collectionRate}% collection rate accounts for the blended payment mix and installment lapse risk,
                    yielding an effective premium of ${Math.round(policyValue * collectionRate / 100).toLocaleString()} per policy.
                    Profit = revenue x {insuranceMargin}% margin (a simplified proxy for total per-policy economic return
                    including investment income and lapse gains; published company-level margins are lower).
                    Sensitivity analysis varies close rate (20/35/50%), general conversion (0.5/1/2/3%),
                    and margin (15/25/35%) independently to show profit range across plausible assumption sets.
                </p>
            </div>
        </div>
    );
}
